import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { collection, getDocs, updateDoc, addDoc, doc } from 'firebase/firestore';
import { db } from '../firebase';
import { schoolClassSortOrder } from '../services/TimetableService';
import './AdminTeachersView.css';
import './SuperAdminDetailPages.css';
import StatCards from './StatCards';

function SuperAdminTeachersView({ adminData }) {
  const [saSchoolId, setSaSchoolId] = useState('');
  const [saSchools, setSaSchools] = useState([]);
  useEffect(() => {
    getDocs(collection(db, 'schools')).then(snap => setSaSchools(snap.docs.map(d => ({ id: d.id, ...d.data() })))).catch(() => {});
  }, []);
  adminData = useMemo(() => ({ ...(adminData || {}), schoolId: saSchoolId || adminData?.schoolId || null }), [adminData, saSchoolId]);
  const [teachers, setTeachers] = useState([]);
  const [classes, setClasses] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editTeacher, setEditTeacher] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedTeacher, setSelectedTeacher] = useState(null);

  const [newTeacher, setNewTeacher] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    username: '',
    subjects: [],
    teacherType: 'regular'
  });

  const availableSubjects = ["Mathematics", "English", "Science", "Biology", "Chemistry", "Social Studies", "History", "Spanish", "French", "Information Technology", "Physical Education", "Arts", "Agriculture", "Principles of Accounts (POA)", "Principles of Business (POB)", "Building Technology", "Home Economics", "Woodwork", "Reading Comprehension", "Penmanship"];

  const loadTeachers = useCallback(async () => {
    console.log('🔥 AdminTeachers: Loading teachers, schoolId:', adminData?.schoolId);
    try {
      const snapshot = await getDocs(collection(db, 'users'));
      let usersData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      // Super Admin: no school selected = national view, load all teachers
      const sid = (adminData?.schoolId || '').toLowerCase();
      if (sid) {
        usersData = usersData.filter(u => (u.schoolId || '').toLowerCase() === sid);
      }
      // Show ALL teachers, no type filtering
      const teachersData = usersData.filter(user => user.role === 'Teacher');
      console.log('🔥 AdminTeachers: Teachers loaded', teachersData.length, teachersData);
      setTeachers(teachersData);
    } catch (error) {
      console.error('🔥 AdminTeachers: Error loading teachers:', error);
    } finally {
      setLoading(false);
    }
  }, [adminData?.schoolId]);

  const loadClasses = useCallback(async () => {
    console.log('🔥 AdminTeachers: Loading classes, schoolId:', adminData?.schoolId);
    try {
      const snapshot = await getDocs(collection(db, 'classes'));
      let classesData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      // Super Admin: no school selected = national view, load all classes
      const sid = (adminData?.schoolId || '').toLowerCase();
      if (sid) {
        classesData = classesData.filter(c => (c.schoolId || '').toLowerCase() === sid);
      }
      console.log('🔥 AdminTeachers: Classes loaded', classesData.length, classesData);
      classesData.sort(schoolClassSortOrder);
      setClasses(classesData);
    } catch (error) {
      console.error('🔥 AdminTeachers: Error loading classes:', error);
    }
  }, [adminData?.schoolId]);

  useEffect(() => {
    loadTeachers();
    loadClasses();
  }, [loadTeachers, loadClasses]);

  // Resolve schoolId -> school name (match doc id, uuid field, or name; case-insensitive)
  const resolveSchoolName = (schoolId) => {
    if (!schoolId) return 'N/A';
    const sid = String(schoolId).toLowerCase();
    const match = saSchools.find(s =>
      String(s.id).toLowerCase() === sid ||
      String(s.uuid || '').toLowerCase() === sid ||
      String(s.name || '').toLowerCase() === sid
    );
    return match ? (match.name || match.id) : String(schoolId);
  };

  const getFilteredTeachers = () => {
    if (!searchText) {
      return teachers;
    }
    const searchLower = searchText.toLowerCase();
    return teachers.filter(teacher =>
      teacher.firstName?.toLowerCase().includes(searchLower) ||
      teacher.lastName?.toLowerCase().includes(searchLower) ||
      teacher.email?.toLowerCase().includes(searchLower) ||
      teacher.username?.toLowerCase().includes(searchLower)
    );
  };

  // Classes assigned to this teacher: match class.teacherId against teacher
  // doc id / uid, or class doc id inside teacher.assignedClassIds
  const getTeacherClasses = (teacher) => {
    const ids = [teacher.id, teacher.uid].filter(Boolean).map(x => String(x).toLowerCase());
    const assigned = (teacher.assignedClassIds || []).map(x => String(x).toLowerCase());
    return classes.filter(cls =>
      ids.includes(String(cls.teacherId || '').toLowerCase()) ||
      assigned.includes(String(cls.id).toLowerCase())
    );
  };

  const handleAddTeacher = async (e) => {
    e.preventDefault();
    try {
      const teacherData = {
        ...newTeacher,
        role: 'Teacher',
        schoolId: adminData?.schoolId || null,
        createdAt: new Date().toISOString(),
        isActive: true
      };
      await addDoc(collection(db, 'users'), teacherData);
      setShowAddForm(false);
      setNewTeacher({
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        username: '',
        subjects: [],
        teacherType: 'regular'
      });
      loadTeachers();
    } catch (error) {
      console.error('Error adding teacher:', error);
    }
  };

  const handleEditTeacher = (teacher) => {
    setIsEditing(true);
    setEditTeacher(teacher);
    setNewTeacher({
      firstName: teacher.firstName || '',
      lastName: teacher.lastName || '',
      email: teacher.email || '',
      phoneNumber: teacher.phoneNumber || '',
      username: teacher.username || '',
      subjects: teacher.specialtySubjects || [],
      teacherType: teacher.teacherType || 'regular'
    });
    setShowAddForm(true);
  };

  const handleUpdateTeacher = async (e) => {
    e.preventDefault();
    try {
      // Only update editable fields — username and email stay as-is from editTeacher
      await updateDoc(doc(db, 'users', editTeacher.id), {
        firstName: newTeacher.firstName,
        lastName: newTeacher.lastName,
        phoneNumber: newTeacher.phoneNumber,
        specialtySubjects: newTeacher.subjects,
        teacherType: newTeacher.teacherType,
        updatedAt: new Date().toISOString()
      });
      setShowAddForm(false);
      setIsEditing(false);
      setEditTeacher(null);
      setNewTeacher({
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        username: '',
        subjects: [],
        teacherType: 'regular'
      });
      loadTeachers();
    } catch (error) {
      console.error('Error updating teacher:', error);
    }
  };

  const handleDeleteTeacher = async (teacherId) => {
    if (!window.confirm('Are you sure you want to delete this teacher?')) return false;
    try {
      await updateDoc(doc(db, 'users', teacherId), {
        isActive: false,
        deletedAt: new Date().toISOString()
      });
      loadTeachers();
      return true;
    } catch (error) {
      console.error('Error deleting teacher:', error);
      return false;
    }
  };

  const handleSubjectToggle = (subject) => {
    setNewTeacher(prev => {
      const subjects = prev.subjects || [];
      if (subjects.includes(subject)) {
        return { ...prev, subjects: subjects.filter(s => s !== subject) };
      } else {
        return { ...prev, subjects: [...subjects, subject] };
      }
    });
  };

  const resetForm = () => {
    setShowAddForm(false);
    setIsEditing(false);
    setEditTeacher(null);
    setNewTeacher({
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
      username: '',
      subjects: [],
      teacherType: 'regular'
    });
  };

  if (loading) {
    return <div className="loading">Loading teachers...</div>;
  }

  // Full-screen teacher detail page — replaces the list (not an overlay)
  if (selectedTeacher) {
    const t = selectedTeacher;
    const teacherClasses = getTeacherClasses(t);
    const photo = t.photoURL || t.photoUrl || t.profilePhoto || t.photo;
    return (
      <div className="detail-page">
        <button className="dp-back" onClick={() => setSelectedTeacher(null)}>
          ◄ Back to Teachers
        </button>

        <div className="dp-hero">
          <div className="dp-avatar">
            {photo
              ? <img src={photo} alt="" />
              : (t.firstName ? t.firstName[0].toUpperCase() : (t.username ? t.username[0].toUpperCase() : 'T'))}
          </div>
          <div className="dp-hero-text">
            <h2>{`${t.firstName || ''} ${t.lastName || ''}`.trim() || t.username || 'Unknown'}</h2>
            <p>{t.email || 'No email'}</p>
            <span className="dp-badge blue">
              {t.teacherType === 'subject' ? 'Subject Teacher' : 'Class Teacher'}
            </span>
            <span className={`dp-badge ${t.isActive === false ? 'red' : 'green'}`}>
              {t.isActive === false ? 'Inactive' : 'Active'}
            </span>
          </div>
        </div>

        <div className="dp-card">
          <h3>Teacher Information</h3>
          <div className="dp-grid">
            <div className="dp-field">
              <div className="dp-label">Name</div>
              <div className="dp-value">{`${t.firstName || ''} ${t.lastName || ''}`.trim() || 'N/A'}</div>
            </div>
            <div className="dp-field">
              <div className="dp-label">Email</div>
              <div className="dp-value">{t.email || 'N/A'}</div>
            </div>
            <div className="dp-field">
              <div className="dp-label">Username</div>
              <div className="dp-value">{t.username || 'N/A'}</div>
            </div>
            <div className="dp-field">
              <div className="dp-label">Phone</div>
              <div className="dp-value">{t.phoneNumber || 'N/A'}</div>
            </div>
            <div className="dp-field">
              <div className="dp-label">School</div>
              <div className="dp-value">{resolveSchoolName(t.schoolId)}</div>
            </div>
            <div className="dp-field">
              <div className="dp-label">Teacher Type</div>
              <div className="dp-value">{t.teacherType === 'subject' ? 'Subject Teacher' : 'Class Teacher'}</div>
            </div>
            <div className="dp-field">
              <div className="dp-label">Class Count</div>
              <div className="dp-value">{teacherClasses.length}</div>
            </div>
            <div className="dp-field">
              <div className="dp-label">Status</div>
              <div className="dp-value">{t.isActive === false ? 'Inactive' : 'Active'}</div>
            </div>
            <div className="dp-field">
              <div className="dp-label">Created</div>
              <div className="dp-value">{t.createdAt ? new Date(t.createdAt).toLocaleDateString() : 'N/A'}</div>
            </div>
            <div className="dp-field full">
              <div className="dp-label">User ID</div>
              <div className="dp-value">{t.id}</div>
            </div>
            <div className="dp-field full">
              <div className="dp-label">Specialty Subjects</div>
              <div className="dp-value">
                {t.specialtySubjects?.length > 0 ? t.specialtySubjects.join(', ') : 'None'}
              </div>
            </div>
          </div>
        </div>

        <div className="dp-card">
          <h3>Assigned Classes ({teacherClasses.length})</h3>
          {teacherClasses.length === 0 ? (
            <p className="dp-empty">No classes assigned</p>
          ) : (
            teacherClasses.map(cls => (
              <div key={cls.id} className="dp-row">
                <span className="dp-row-main">{cls.name}</span>
                <span className="dp-row-sub">{cls.grade || ''}</span>
              </div>
            ))
          )}
        </div>

        <div className="dp-actions">
          <button
            type="button"
            className="dp-btn-primary"
            onClick={() => { const teacher = t; setSelectedTeacher(null); handleEditTeacher(teacher); }}
          >
            Edit
          </button>
          <button
            type="button"
            className="dp-btn-danger"
            onClick={async () => { if (await handleDeleteTeacher(t.id)) setSelectedTeacher(null); }}
          >
            Delete
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-teachers-view">
      <div className="header">
        <h2>Teacher Management</h2>
        <div className="sa-school-filter"><label>School:</label><select value={saSchoolId} onChange={(e) => setSaSchoolId(e.target.value)}><option value="">All Schools</option>{saSchools.map(s => <option key={s.id} value={s.id}>{s.name || s.id}</option>)}</select></div>
        <button className="add-button" onClick={() => { resetForm(); setShowAddForm(true); }}>
          + Add Teacher
        </button>
      </div>
      <StatCards items={[['Teachers',teachers.length],['Home Teachers',teachers.filter(t => t.teacherType !== 'subject').length],['Subject Teachers',teachers.filter(t => t.teacherType === 'subject').length],['Unassigned',teachers.filter(t => t.teacherType !== 'subject' && !classes.some(c => c.teacherId === t.id || c.teacherId === t.uid)).length],['Classes',classes.length]]} />

      <div className="search-bar">
        <input
          type="text"
          placeholder="Search teachers..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />
      </div>

      <div className="teachers-list">
        {getFilteredTeachers().length === 0 ? (
          <p className="no-results">No teachers found</p>
        ) : (
          getFilteredTeachers().map(teacher => {
            const teacherClasses = getTeacherClasses(teacher);
            return (
              <div key={teacher.id} className="teacher-card" onClick={() => setSelectedTeacher(teacher)}>
                <div className="teacher-avatar">
                  {teacher.firstName ? teacher.firstName[0].toUpperCase() : (teacher.username ? teacher.username[0].toUpperCase() : 'T')}
                </div>
                <div className="teacher-info">
                  <h3>{teacher.firstName || ''} {teacher.lastName || ''}</h3>
                  <p className="teacher-email">{teacher.email || 'No email'}</p>
                  <p className="teacher-meta">
                    <span className="teacher-type-badge">{teacher.teacherType === 'subject' ? 'Subject Teacher' : 'Class Teacher'}</span>
                    {teacherClasses.length > 0 && <span className="teacher-classes">{teacherClasses.length} classes</span>}
                  </p>
                </div>
                <div className="teacher-actions">
                  <button className="edit-button" onClick={(e) => { e.stopPropagation(); handleEditTeacher(teacher); }}>
                    Edit
                  </button>
                  <button className="delete-button" onClick={(e) => { e.stopPropagation(); handleDeleteTeacher(teacher.id); }}>
                    Delete
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {showAddForm && (
        <div className="modal-overlay" onClick={resetForm}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>{isEditing ? 'Edit Teacher' : 'Add Teacher'}</h3>
            <form onSubmit={isEditing ? handleUpdateTeacher : handleAddTeacher}>
              <div className="form-group">
                <label>First Name *</label>
                <input
                  type="text"
                  value={newTeacher.firstName}
                  onChange={(e) => setNewTeacher({ ...newTeacher, firstName: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Last Name *</label>
                <input
                  type="text"
                  value={newTeacher.lastName}
                  onChange={(e) => setNewTeacher({ ...newTeacher, lastName: e.target.value })}
                  required
                />
              </div>
              {!isEditing && (
                <>
                  <div className="form-group">
                    <label>Email *</label>
                    <input
                      type="email"
                      value={newTeacher.email}
                      onChange={(e) => setNewTeacher({ ...newTeacher, email: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Username *</label>
                    <input
                      type="text"
                      value={newTeacher.username}
                      onChange={(e) => setNewTeacher({ ...newTeacher, username: e.target.value })}
                      required
                    />
                  </div>
                </>
              )}
              <div className="form-group">
                <label>Phone Number</label>
                <input
                  type="tel"
                  value={newTeacher.phoneNumber}
                  onChange={(e) => setNewTeacher({ ...newTeacher, phoneNumber: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Teacher Type</label>
                <select
                  value={newTeacher.teacherType}
                  onChange={(e) => setNewTeacher({ ...newTeacher, teacherType: e.target.value })}
                >
                  <option value="regular">Class Teacher</option>
                  <option value="subject">Subject Teacher</option>
                </select>
              </div>
              <div className="form-group">
                <label>Specialty Subjects</label>
                <div className="subjects-grid">
                  {availableSubjects.map(subject => (
                    <label key={subject} className="subject-checkbox">
                      <input
                        type="checkbox"
                        checked={newTeacher.subjects?.includes(subject) || false}
                        onChange={() => handleSubjectToggle(subject)}
                      />
                      <span>{subject}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="form-actions">
                <button type="button" onClick={resetForm}>Cancel</button>
                <button type="submit">{isEditing ? 'Update' : 'Add'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default SuperAdminTeachersView;
